import React, { useState, useEffect, useContext, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Tabs, 
  Tab, 
  CircularProgress, 
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, isToday } from 'date-fns';
import { pl } from 'date-fns/locale';

// Import komponentów
import GeneralStats from './components/GeneralStats';
import SalesCharts from './components/SalesCharts';
import TopPerformers from './components/TopPerformers';
import EmployeeStats from './components/EmployeeStats';
import WorkshopStats from './components/WorkshopStats';
import LocationStats from './components/LocationStats';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    products: [],
    sales: [],
    orders: [],
    workshop: [],
    clients: [],
    users: []
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const isAdmin = currentUser?.role === 'admin';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchDashboardData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    console.log(`[DASHBOARD LOG] Rozpoczynam pobieranie danych dla ${isAdmin ? 'ADMINA' : 'PRACOWNIKA'} (${currentUser.email})`);
    try {
      const startDate = startOfMonth(selectedMonth);
      const endDate = endOfMonth(selectedMonth);
      console.log(`[DASHBOARD LOG] Zakres dat: ${format(startDate, 'yyyy-MM-dd')} - ${format(endDate, 'yyyy-MM-dd')}`);
      
      let productsData = [], salesData = [], ordersData = [], workshopData = [], clientsData = [], usersData = [];

      // Pobieranie danych sprzedaży
      const salesQuery = isAdmin 
        ? query(
            collection(db, 'sales'),
            where('status', '==', 'completed'),
            where('completedAt', '>=', startDate),
            where('completedAt', '<=', endDate)
          )
        : query(
            collection(db, 'sales'),
            where('employee_id', '==', currentUser.uid),
            where('completedAt', '>=', startDate),
            where('completedAt', '<=', endDate)
          );
      console.log('[DASHBOARD LOG] Zapytanie o sprzedaż:', salesQuery);
      const salesSnapshot = await getDocs(salesQuery);
      console.log(`[DASHBOARD LOG] Pobrano ${salesSnapshot.size} dokumentów sprzedaży.`);
      salesData = salesSnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data, 
          createdAt: data.createdAt?.toDate(),
          completedAt: data.completedAt?.toDate() 
        };
      });

      // Pobieranie danych o klientach
      if (isAdmin) {
        console.log('[DASHBOARD LOG] Pobieranie danych o klientach...');
        const clientsQuery = query(collection(db, 'clients'));
        const clientsSnapshot = await getDocs(clientsQuery);
        console.log(`[DASHBOARD LOG] Pobrano ${clientsSnapshot.size} klientów.`);
        clientsData = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } else {
        // Dla pracownika, pobieramy tylko klientów powiązanych z jego sprzedażą
        if (salesData.length > 0) {
          const clientIds = [...new Set(salesData.map(sale => sale.clientId).filter(Boolean))];
          if (clientIds.length > 0) {
            console.log(`[DASHBOARD LOG] Pobieranie danych dla ${clientIds.length} klientów pracownika...`);
            const clientPromises = clientIds.map(id => getDoc(doc(db, 'clients', id)));
            const clientDocs = await Promise.all(clientPromises);
            clientsData = clientDocs.map(docSnap => docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null).filter(Boolean);
            console.log(`[DASHBOARD LOG] Pomyślnie pobrano ${clientsData.length} klientów.`);
          }
        }
      }

      // Pobieranie danych o użytkownikach (pracownikach)
      if (isAdmin) {
        console.log('[DASHBOARD LOG] Pobieranie danych o użytkownikach (pracownikach)...');
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        console.log(`[DASHBOARD LOG] Pobrano ${usersSnapshot.size} użytkowników.`);
        usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } else {
        // Dla pracownika pobieramy tylko jego dane
        console.log(`[DASHBOARD LOG] Pobieranie danych dla zalogowanego pracownika: ${currentUser.uid}`);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          console.log('[DASHBOARD LOG] Znaleziono dane pracownika.');
          usersData = [{ id: userDoc.id, ...userDoc.data() }];
        } else {
          console.log('[DASHBOARD LOG] Nie znaleziono danych dla pracownika.');
        }
      }

      // Pobieranie danych zamówień
      const ordersQuery = isAdmin 
        ? query(
            collection(db, 'orders'),
            where('createdAt', '>=', startDate),
            where('createdAt', '<=', endDate)
          )
        : query(
            collection(db, 'orders'),
            where('employee_id', '==', currentUser.uid),
            where('createdAt', '>=', startDate),
            where('createdAt', '<=', endDate)
          );
      console.log('[DASHBOARD LOG] Zapytanie o zamówienia:', ordersQuery);
      const ordersSnapshot = await getDocs(ordersQuery);
      console.log(`[DASHBOARD LOG] Pobrano ${ordersSnapshot.size} dokumentów zamówień.`);
      ordersData = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data, 
          createdAt: data.createdAt?.toDate()
        };
      });

      // Jeśli nie jest adminem, pobierz klientów powiązanych ze sprzedażą
      if (!isAdmin && salesData.length > 0) {
        const clientIds = [...new Set(salesData.map(sale => sale.client_id).filter(id => id))];
        
        if (clientIds.length > 0) {
          console.log(`[DASHBOARD LOG] Znaleziono ${clientIds.length} unikalnych ID klientów. Pobieranie danych...`);
          // Firestore pozwala na maksymalnie 30 wartości w zapytaniu 'in'
          // Dzielimy zapytanie na mniejsze części, jeśli jest to konieczne
          const clientChunks = [];
          for (let i = 0; i < clientIds.length; i += 30) {
            clientChunks.push(clientIds.slice(i, i + 30));
          }

          const clientPromises = clientChunks.map(chunk => 
            getDocs(query(collection(db, 'clients'), where('__name__', 'in', chunk)))
          );

          const clientSnapshots = await Promise.all(clientPromises);
          clientSnapshots.forEach(snapshot => {
            const chunkData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            clientsData.push(...chunkData);
          });

          console.log(`[DASHBOARD LOG] Pobrano ${clientsData.length} dokumentów klientów.`);
        }
      }

      // Pobieranie danych dla administratora
      if (isAdmin) {
        // Pobieranie danych produktów
        const productsQuery = query(collection(db, 'products'));
        const workshopQuery = query(collection(db, 'workshop'));
        const clientsQuery = query(collection(db, 'clients'));
        const usersQuery = query(collection(db, 'users'));

        const [productsSnap, workshopSnap, clientsSnap, usersSnap] = await Promise.all([
          getDocs(productsQuery),
          getDocs(workshopQuery),
          getDocs(clientsQuery),
          getDocs(usersQuery)
        ]);

        productsData = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        workshopData = workshopSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        clientsData = clientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        usersData = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log(`[DASHBOARD LOG] Admin: Pobrano ${productsData.length} produktów, ${workshopData.length} zleceń, ${clientsData.length} klientów, ${usersData.length} użytkowników.`);
      } else {
        // Pobieranie danych dla pracownika
        const clientsQuery = query(collection(db, 'clients'), where('assigned_to', '==', currentUser.uid));
        const usersQuery = query(collection(db, 'users'));
        const [clientsSnapshot, usersSnapshot] = await Promise.all([
          getDocs(clientsQuery),
          getDocs(usersQuery)
        ]);

        clientsData = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log(`[DASHBOARD LOG] Pracownik: Pobrano ${clientsData.length} klientów i ${usersData.length} użytkowników.`);
      }

      const finalData = { products: productsData, sales: salesData, orders: ordersData, workshop: workshopData, clients: clientsData, users: usersData };
      console.log('[DASHBOARD LOG] Ostateczne dane do ustawienia w stanie:', finalData);
      setDashboardData(finalData);

    } catch (error) {
      console.error('[DASHBOARD LOG] KRYTYCZNY BŁĄD podczas pobierania danych:', error);
      setSnackbar({ open: true, message: 'Błąd pobierania danych dashboardu.', severity: 'error' });
    } finally {
      console.log('[DASHBOARD LOG] Zakończono pobieranie danych.');
      setLoading(false);
    }
  }, [selectedMonth, currentUser, isAdmin]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData();
    setSnackbar({ open: true, message: 'Dane zostały odświeżone', severity: 'success' });
  };

  const renderTabs = () => {
    const adminTabs = [
      { label: "Przegląd" },
      { label: "Sprzedaż" },
      { label: "Pracownia" },
      { label: "Najlepsi" },
      { label: "Lokalizacje" },
    ];
    const employeeTabs = [
      { label: "Moje statystyki" },
      { label: "Moja sprzedaż" },
      { label: "Moi klienci" },
    ];
    const tabs = isAdmin ? adminTabs : employeeTabs;

    return (
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant={isMobile ? "scrollable" : "fullWidth"}
        scrollButtons={isMobile ? "auto" : false}
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab, index) => <Tab key={index} label={tab.label} />)}
      </Tabs>
    );
  };

  const renderAdminContent = () => {
    switch (tabValue) {
      case 0: // Przegląd
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <GeneralStats data={dashboardData} selectedMonth={selectedMonth} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SalesCharts data={dashboardData.sales} products={dashboardData.products} selectedMonth={selectedMonth} chartType="monthly" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TopPerformers data={dashboardData} type="employees" users={dashboardData.users} selectedMonth={selectedMonth} />
            </Grid>
          </Grid>
        );
      case 1: // Sprzedaż
        return <SalesCharts data={dashboardData.sales} products={dashboardData.products} selectedMonth={selectedMonth} chartType="detailed" />;
      case 2: // Pracownia
        return <WorkshopStats data={dashboardData.workshop} />;
      case 3: // Najlepsi
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TopPerformers data={dashboardData} type="clients" users={dashboardData.clients} selectedMonth={selectedMonth} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TopPerformers data={dashboardData} type="employees" users={dashboardData.users} selectedMonth={selectedMonth} />
            </Grid>
          </Grid>
        );
      case 4: // Lokalizacje
        return <LocationStats data={dashboardData} selectedMonth={selectedMonth} />;
      default:
        return null;
    }
  };

  const renderEmployeeContent = () => {
    switch (tabValue) {
      case 0: // Moje statystyki
        return <EmployeeStats employeeId={currentUser.uid} sales={dashboardData.sales} clients={dashboardData.clients} employees={dashboardData.users} selectedMonth={selectedMonth} />;
      case 1: // Moja sprzedaż
        return <SalesCharts data={dashboardData.sales} products={dashboardData.products} selectedMonth={selectedMonth} chartType="simple" />;
      case 2: // Moi klienci
        return <TopPerformers data={dashboardData} type="clients" employeeId={currentUser.uid} users={dashboardData.clients} selectedMonth={selectedMonth} />;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom sx={{ mb: { xs: 1, sm: 0 } }}>
            Dashboard - {format(selectedMonth, 'LLLL yyyy', { locale: pl })}
          </Typography>
          <Box>
            <IconButton onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={() => setSelectedMonth(new Date())} disabled={isToday(selectedMonth)}>
              <TodayIcon />
            </IconButton>
            <IconButton onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}>
              <ChevronRightIcon />
            </IconButton>
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        {renderTabs()}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          isAdmin ? renderAdminContent() : renderEmployeeContent()
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Dashboard;
